/* eslint-disable @typescript-eslint/naming-convention */
import { ISiteConfig } from '../IApiResponses';
import { IConfig } from '../../domain/entities/Config';
import { getAddressDisplaySchema } from '../../utils/AddressUtils';
import GOOGLE_MAP_STYLE from '../../constants/GoogleMapStylers';

const siteConfigMapper = {
  response(resp: ISiteConfig): IConfig {
    const {
      address_config,
      shipping_address_input_schema,
      billing_address_input_schema,
      contact_info_input_schema,
      packstation_address_input_schema,
      base_url: baseUrl,
      brand_id: brandId,
      brand_name: brandName,
      region_data: {
        billing_countries: deprecatedBillingCountries,
        allowed_billing_countries: billingCountries,
        shipping_countries: deprecatedShippingCountries,
        allowed_shipping_countries: shippingCountries,
        allowed_packstation_countries: packstationCountries,
        states,
        select_cities: cities,
      },
      region_id: regionId,
      region_name: regionName,
      country_id: newCountryId,
      currency,
      courier,
      locale,
      payment_instructions,
      site_config,
      site_config: {
        accepted_privacy_policy,
        backend_validation,
        adv_legal: advancedLegal,
        allow_gc_without_cc,
        checkout_idle_timeout: sessionTimeout,
        checkout_use_new_design: useNewAddressDesign,
        is_melissa_enabled: isMelissaEnabled,
        is_production: isProduction,
        use_ups_validation_at_checkout: useUpsValidation,
        use_city_state_lookup: useCityStateLookup,
        use_shipping_calculator: useShippingCalculator,
        tnc_content_node_id: tncContentNodeId,
        klarna_popup_node_id: klarnaContentNodeId,
        //TODO: gift_wrap - https://jira.esteeonline.com/browse/UKPRGT-3368
        giftwrap: giftWrap,
        show_free_label_as_price: showFreeLabelAsPrice,
        show_strikeoff_price: showStrikeoffPrice,
        display_included_tax: displayIncludedTax,
        country_taxes_label: countryTaxesLabel,
        use_json_schemas_for_address_validation: jsonSchemasEnabled,
        display_shipping_fee_with_tax: displayShippingFeeWithTax,
        display_delivery_date: displayDeliveryDate,
        has_vat: vatPercentage,
        max_gift_card_number_length: maxGiftCardNumberLength,
        privacy_policy_url: privacyPolicyUrl,
        google_maps_key: googleMapsKey,
        use_e164_phone_standard: useE164PhoneStandard,
        enable_donation: isCharityDonationEnabled,
        enable_wallet_payment: isWalletPaymentEnabled,
      },
      store_cost_limit: convenienceStoreLimit,
      display_store_collection: displayStoreCollection,
      rco_ups_enabled: isClickAndCollect,
      rco_bopis_enabled: bopisEnabled,
      rco_bopis_gift_wrap_enabled: bopisGiftWrapEnabled,
      rco_courier_enabled: isSameDayEnabled,
      rco_salon_enabled: salonCommissionEnabled,
      salon_data_share_enabled: isSalonDataShareEnabled,
      // async_address: condensedAddressEnabled, RCO-7996 - Disabling limited view of Addresses
      condensed_fields: condensedFields,
      rco_edd_enabled: isEstimatedDeliveryDateEnabled,
      rco_split_shipment_enabled: isSplitShipmentEnabled,
      newsletter,
      stardust,
      bill_to_company,
      bill_to_company_schema,
      show_invoice_section: showInvoiceSection,
      invoice_carrier_types: invoiceCarrierTypeOptions,
      invoice_donation_love_codes: invoiceDonationLoveCodeOptions,
      static_lookup_urls,
      display_our_guarantee_on,
      display_store_name_with_address,
      routes,
      country_phone_verification_schema,
      google_map_style,
      beacon_script: beaconScript,
    } = resp;

    const countryId = newCountryId || site_config.site.country_id;

    // remove deprecated properties when safe
    let shippingAddressInputSchema;
    if (shipping_address_input_schema) {
      shippingAddressInputSchema = shipping_address_input_schema.properties;
    } else if (address_config) {
      const {
        shipping_address_input_schema: deprecatedShippingSchema,
      } = address_config;
      shippingAddressInputSchema = deprecatedShippingSchema.properties
        || deprecatedShippingSchema.address.properties;
    }

    // remove deprecated propeties when safe
    const billingAddressInputSchema = billing_address_input_schema
      ? billing_address_input_schema.properties
      : address_config.billing_address_input_schema.properties;

    const packstationAddressInputSchema = packstation_address_input_schema
      ? packstation_address_input_schema.properties
      : null;

    const contactInfoInputSchema = contact_info_input_schema?.properties;

    const billingAddressDisplaySchema = getAddressDisplaySchema(billingAddressInputSchema);
    const shippingAddressDisplaySchema = getAddressDisplaySchema(shippingAddressInputSchema);

    // remove deprecated properties when safe
    // eslint-disable max-len
    const defaultTermsConditions = accepted_privacy_policy?.default_terms_conditions;

    // remove deprecated properties when safe
    const messageOnlyTermsConditions = accepted_privacy_policy?.message_only_terms_conditions;

    // eslint-enable max-len

    // remove deprecated properties when safe
    const canCheckoutWithoutPayment = Boolean(allow_gc_without_cc);

    const isSameDayDeliveryAvailable = courier && !!isSameDayEnabled;

    const {
      businessUnitId,
      clientId,
      defaultDomain,
      environment,
    } = { ...stardust };

    // Check to see if company billing is activated
    const canSetCompanyBillingEnabled = !!bill_to_company;

    const billToCompanySchema = bill_to_company_schema
      ? bill_to_company_schema.properties
      : null;

    const companyBillingDisplaySchema = getAddressDisplaySchema(
      billToCompanySchema,
    );

    // Will handle showing the OurGuarantee section on both delivery and payment page
    // By default, the section should be displayed on the delivery_page
    const displayOurGuaranteeOn = {
      deliveryPage: display_our_guarantee_on ? !!display_our_guarantee_on.delivery_page : true,
      paymentPage: !!display_our_guarantee_on?.payment_page || false,
    };

    return {
      advancedLegal: !!advancedLegal,
      backendValidation: {
        countryId: backend_validation?.country_id,
        shouldValidateSavedShippingAddress: backend_validation?.should_validate_saved_shipping_address,
        validationType: backend_validation?.validation_type,
      },
      baseUrl: baseUrl || window.location.origin,
      beaconScript,
      billToCompanySchema,
      billingAddressDisplaySchema,
      billingAddressInputSchema,
      billingCountries: billingCountries || deprecatedBillingCountries,
      bopisEnabled: !!bopisEnabled,
      bopisGiftWrapEnabled: !!bopisGiftWrapEnabled,
      brandId,
      brandName,
      canCheckoutWithoutPayment,
      canSetCompanyBilling: canSetCompanyBillingEnabled,
      cities,
      companyBillingDisplaySchema,
      condensedAddressEnabled: false,
      condensedFields,
      contactInfoInputSchema,
      convenienceStoreLimit,
      countryId,
      countryTaxesLabel: !!countryTaxesLabel,
      currency: currency || site_config.site.currency,
      defaultTermsConditions: !!defaultTermsConditions,
      displayDeliveryDate: !!displayDeliveryDate,
      displayIncludedTax: !!displayIncludedTax,
      displayOurGuaranteeOn,
      displayShippingFeeWithTax: !!displayShippingFeeWithTax,
      displayStoreCollection: !!displayStoreCollection,
      externalRoutes: routes,
      giftWrap: !!giftWrap,
      googleMapStyle: google_map_style as GOOGLE_MAP_STYLE,
      googleMapsKey,
      invoiceCarrierTypeOptions,
      invoiceDonationLoveCodeOptions,
      isCharityDonationEnabled: !!isCharityDonationEnabled,
      isClickAndCollect: !!isClickAndCollect,
      isEstimatedDeliveryDateEnabled: !!isEstimatedDeliveryDateEnabled,
      isMelissaEnabled: !!isMelissaEnabled,
      isProduction: !!isProduction,
      isSalonDataShareEnabled: !!isSalonDataShareEnabled,
      isSameDayDeliveryAvailable,
      isSplitShipmentEnabled: !!isSplitShipmentEnabled,
      isWalletPaymentEnabled: !!isWalletPaymentEnabled,
      jsonSchemasEnabled: !!jsonSchemasEnabled,
      klarnaContentNodeId,
      locale,
      maxGiftCardNumberLength,
      messageOnlyTermsConditions: !!messageOnlyTermsConditions,
      newsletter,
      packstationAddressInputSchema,
      packstationCountries,
      paymentInstructions: Boolean(payment_instructions),
      phonePatterns: country_phone_verification_schema,
      privacyPolicyUrl,
      regionId,
      regionName,
      salonCommissionEnabled: !!salonCommissionEnabled,
      sessionTimeout,
      shippingAddressDisplaySchema, // remove deprecated code when it's safe
      shippingAddressInputSchema,
      shippingCountries: shippingCountries || deprecatedShippingCountries,
      showFreeLabelAsPrice: !!showFreeLabelAsPrice,
      showInvoiceSection: !!showInvoiceSection,
      showStoreNameWithAddress: !!display_store_name_with_address,
      showStrikeoffPrice: !!showStrikeoffPrice,
      stardustServiceConfig: {
        baseUrl: defaultDomain,
        businessUnitId,
        clientId,
        environment,
      },
      states,
      staticDataLookupUrls: static_lookup_urls,
      tncContentNodeId,
      useCityStateLookup: !!useCityStateLookup,
      useE164PhoneStandard: !!useE164PhoneStandard,
      useNewAddressDesign: !!useNewAddressDesign,
      useShippingCalculator: !!useShippingCalculator,
      useUpsValidation: !!useUpsValidation,
      vatPercentage,
    };
  },
};

export default siteConfigMapper;
